import type { FrontendBlog } from './api.models';

/**
 * Plain-text source for a blog card's excerpt: the full `content` on detail payloads, or the
 * server-cut `excerpt` that card payloads (`/blogs?fields=card`) send instead of the body.
 */
export function blogBodyText(blog: Pick<FrontendBlog, 'content' | 'excerpt'>): string {
  return blog.content ?? blog.excerpt ?? '';
}

/**
 * Words in a post, for read-time labels. Card payloads carry the server's count and no body; full
 * payloads are counted here from the body, as every listing did before card payloads existed.
 */
export function blogWordCount(
  blog: Pick<FrontendBlog, 'wordCount' | 'content' | 'shortDescription' | 'name'>,
): number {
  if (typeof blog.wordCount === 'number' && blog.wordCount > 0) {
    return blog.wordCount;
  }

  const text = String(blog.content ?? blog.shortDescription ?? blog.name ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  return text ? text.split(/\s+/).length : 0;
}

/** Reading speed behind every "N min read" label on the site. */
export const BLOG_READ_WORDS_PER_MINUTE = 200;

/**
 * Minutes to read a post: one rule for every card, list and article. The site used to count 100 words a
 * minute on cards and 200 on the article, so one post read as "12 min" on /blogs and "6 min" on its own page.
 */
export function blogReadMinutes(
  blog: Pick<FrontendBlog, 'wordCount' | 'content' | 'shortDescription' | 'name'>,
): number {
  return Math.max(1, Math.ceil(blogWordCount(blog) / BLOG_READ_WORDS_PER_MINUTE));
}
